import { HttpStatusCode } from 'axios';

import { logger } from './logger';

async function nullIf<T>(
  statusCode: HttpStatusCode,
  functionToExecute: () => Promise<T>
) {
  try {
    return await functionToExecute();
  } catch (error) {
    const message = `Caught an exception when executing ${functionToExecute}`;
    if (error.status === statusCode) {
      logger.warn(error, message);
      return null;
    }
    logger.error(error, message);
    throw error;
  }
}
export async function nullIfNotFound<T>(functionToExecute: () => Promise<T>) {
  return await nullIf(HttpStatusCode.NotFound, functionToExecute);
}

export async function nullIfBadRequest<T>(functionToExecute: () => Promise<T>) {
  return await nullIf(HttpStatusCode.BadRequest, functionToExecute);
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Run `fn` for every item, keeping at most `concurrency` invocations in
 * flight. On failure it stops picking up new items, lets in-flight
 * invocations finish, then rejects with an AggregateError holding every
 * collected failure.
 */
export async function forEachWithConcurrency<T>(
  items: readonly T[],
  concurrency: number,
  fn: (item: T) => Promise<void>
): Promise<void> {
  if (concurrency < 1) {
    throw new Error(`concurrency must be at least 1, got ${concurrency}`);
  }

  const iterator = items[Symbol.iterator]();
  const errors: unknown[] = [];

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    async () => {
      for (let next = iterator.next(); !next.done; next = iterator.next()) {
        if (errors.length > 0) return;
        try {
          await fn(next.value);
        } catch (error) {
          errors.push(error);
        }
      }
    }
  );

  await Promise.all(workers);

  if (errors.length > 0) {
    throw new AggregateError(
      errors,
      `${errors.length} of ${items.length} items failed`
    );
  }
}
